import {
    BookOpen,
    FlaskConical,
    Microscope,
    GraduationCap,
    ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";

const STATS = [
    { k: "15", v: "Categories" },
    { k: "100+", v: "Topics" },
    { k: "250+", v: "Studies Cited" },
    { k: "0", v: "Generic AI Content" },
];

const TRUST_CHIPS = [
    { icon: BookOpen, label: "250+ studies" },
    { icon: Microscope, label: "Clinical context" },
    { icon: FlaskConical, label: "Every TRT marker" },
    { icon: GraduationCap, label: "Practitioner-reviewed" },
];

const CATEGORIES = [
    {
        name: "My Protocol",
        count: "18 topics",
        bullets: [
            "The Perfect Protocol",
            "Managing Side Effects",
            "Bloodwork Timing",
        ],
        more: "+ 15 more",
    },
    {
        name: "Injection Guide",
        count: "7 topics",
        bullets: [
            "IM vs Shallow IM vs SubQ",
            "Ventrogluteal Site Anatomy",
            "Shallow IM Technique",
        ],
        more: "+ 4 more",
    },
    {
        name: "Testosterone Cream",
        count: "4 topics",
        bullets: [
            "Why Sites Absorb Differently",
            "Site-by-Site Absorption Table",
            "Scrotal Application & DHT",
        ],
        more: "+ 1 more",
    },
    {
        name: "From Injection to Excretion",
        count: "7 topics",
        bullets: [
            "Why Esters Are Used",
            "Esterase Breakdown Pathway",
            "Conversion to DHT & Estradiol",
        ],
        more: "+ 4 more",
    },
    {
        name: "Frequency & Scheduling",
        count: "4 topics",
        bullets: [
            "Injection Frequency & Hormone Stability",
            "Cream Circadian Timing",
            "Peak-to-Trough Fluctuation by Frequency",
        ],
        more: "+ 1 more",
    },
    {
        name: "My Biomarkers",
        count: "8 topics",
        bullets: [
            "0–1000 Biomarker Score",
            "T:SHBG Ratio",
            "Personalized Testosterone Range",
        ],
        more: "+ 5 more",
    },
    {
        name: "Estradiol (E2)",
        count: "10 topics",
        bullets: [
            "The 5–6 Month Adaptation Window",
            "Gynecomastia & Androgenic Balance",
            "Cannabis & Estrogenic Symptoms",
        ],
        more: "+ 7 more",
    },
    {
        name: "Hematocrit",
        count: "7 topics",
        bullets: [
            "Oxygen Delivery & Blood Viscosity",
            "The 50% Target — Lessons from Cycling",
            "Dynamic Hematocrit Thresholds",
        ],
        more: "+ 4 more",
    },
    {
        name: "SHBG",
        count: "4 topics",
        bullets: [
            "SHBG Modulation Table (30)",
            "Low SHBG Strategies",
            "High SHBG Strategies",
        ],
        more: "+ 1 more",
    },
    {
        name: "ApoB & Lipids",
        count: "3 topics",
        bullets: [
            "ApoB vs Traditional Lipid Panels",
            "Why ApoB Matters on TRT",
            "HDL Cholesterol",
        ],
        more: null,
    },
    {
        name: "Supporting Biomarkers",
        count: "11 topics",
        bullets: [
            "Cystatin C (vs Creatinine)",
            "DHT (Dihydrotestosterone)",
            "HbA1c, CRP, GGT, TSH, BP",
        ],
        more: "+ 8 more",
    },
    {
        name: "The Testimator",
        count: "7 topics",
        bullets: [
            "Pharmacokinetic Anchoring",
            "Personalized Reference Ranges",
            "Blood Test Timing (Trough vs Peak)",
        ],
        more: "+ 4 more",
    },
    {
        name: "My Free T",
        count: "5 topics",
        bullets: [
            "Why Lab Free T Is Usually Wrong",
            "The Vermeulen Gold Standard & Its Limits",
            "Södergård / Mazer Comparison",
        ],
        more: "+ 2 more",
    },
    {
        name: "My Bloodwork",
        count: "8 topics",
        bullets: [
            "Essential vs Comprehensive Panels",
            "Generating Your AI Report",
            "Testing Best Practices",
        ],
        more: "+ 5 more",
    },
    {
        name: "IGF-1 & Peptides",
        count: "4 topics",
        bullets: [
            "IGF-1 as GH Downstream Mediator",
            "Estradiol's Role in IGF-1 Production",
            "Protocol Design & IGF-1 Output",
        ],
        more: "+ 1 more",
    },
];

export default function KnowledgeBase() {
    return (
        <section
            id="knowledge"
            data-testid="knowledge-section"
            className="relative py-24 lg:py-32 bg-black overflow-hidden border-t border-white/5"
        >
            <div
                className="absolute inset-0 grid-backdrop opacity-25 pointer-events-none"
                aria-hidden
            />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
                {/* Header */}
                <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-end mb-12 lg:mb-16">
                    <div>
                        <div className="eyebrow mb-6">
                            <span className="dot" /> Knowledge Base
                        </div>
                        <h2 className="font-display font-bold clamp-h2 text-white">
                            <span className="blue-glow">The manual</span>
                            <br />
                            TRT never came with.
                        </h2>
                        <p className="mt-6 text-white/65 text-base lg:text-lg leading-relaxed max-w-xl">
                            Every biomarker in your TRT Plus report has its own
                            dedicated breakdown — what it means, how it
                            behaves under TRT, and what moves it in either
                            direction. Backed by published research.
                        </p>
                        <p className="mt-4 text-white/65 text-base lg:text-lg leading-relaxed max-w-xl">
                            Whether you're new to TRT and making sense of your
                            first blood panel, optimizing an advanced protocol,
                            or a clinician wanting a well-researched companion
                            — the depth is there.
                        </p>
                        <div className="mt-7 flex flex-wrap gap-2.5">
                            {TRUST_CHIPS.map((x) => (
                                <div
                                    key={x.label}
                                    className="flex items-center gap-2 bg-white/[0.03] border border-white/10 rounded-full px-4 py-2 text-sm text-white/80"
                                    data-testid={`kb-trust-${x.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                >
                                    <x.icon
                                        size={14}
                                        className="text-[#49bef5]"
                                    />
                                    {x.label}
                                </div>
                            ))}
                        </div>

                        <div className="mt-7">
                            <Link
                                to="/blog"
                                data-testid="kb-blog-link"
                                className="inline-flex items-center gap-2 text-sm text-[#49bef5] hover:text-white transition-colors group"
                            >
                                Read our long-form articles
                                <ArrowRight
                                    size={14}
                                    className="transition-transform group-hover:translate-x-0.5"
                                />
                            </Link>
                        </div>
                    </div>

                    {/* Stat strip */}
                    <div
                        className="grid grid-cols-2 sm:grid-cols-4 border border-white/10 rounded-2xl bg-white/[0.02] divide-x divide-white/10 sm:divide-y-0 divide-y overflow-hidden"
                        data-testid="kb-stats"
                    >
                        {STATS.map((s) => (
                            <div
                                key={s.v}
                                className="px-5 py-5 text-left"
                                data-testid={`kb-stat-${s.v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            >
                                <div className="font-display font-extrabold text-white text-3xl sm:text-4xl leading-none">
                                    {s.k}
                                </div>
                                <div className="mt-2 text-[10px] uppercase tracking-[0.18em] font-mono text-[#49bef5] leading-tight">
                                    {s.v}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* All categories */}
                <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-mono text-[#49bef5] mb-5">
                        All Categories
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {CATEGORIES.map((c) => (
                            <article
                                key={c.name}
                                className="tile p-5 flex flex-col"
                                data-testid={`kb-cat-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            >
                                <div className="flex items-baseline justify-between mb-3 gap-3">
                                    <h3 className="font-display font-semibold text-white text-[16px] leading-tight">
                                        {c.name}
                                    </h3>
                                    <span className="text-[10px] uppercase tracking-[0.18em] font-mono text-[#49bef5] shrink-0">
                                        {c.count}
                                    </span>
                                </div>
                                <ul className="space-y-1.5 mb-3">
                                    {c.bullets.map((b) => (
                                        <li
                                            key={b}
                                            className="flex items-start gap-2 text-[13.5px] text-white/70 leading-snug"
                                        >
                                            <span className="mt-1.5 w-1 h-1 rounded-full bg-[#2ba6e0] shrink-0" />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                                {c.more && (
                                    <div className="mt-auto text-[12px] font-mono text-white/40">
                                        {c.more}
                                    </div>
                                )}
                            </article>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
